import {
  ClipboardList,
  Banknote,
  Sliders,
  Archive,
  Users,
  Building2,
  Network,
  Home,
  Target,
  Landmark,
  TrendingUp,
  Briefcase,
  FileText,
  UserCheck
} from "lucide-react";
import PageAnimator from '../../../components/ui/PageAnimator';
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getFamilles, archiveFamille } from "../../../features/familles/api/familleAPI";
import { getSocietes } from "../../../features/familles/api/societeAPI";
import api from "../../../api";

// Blocs Métiers
import BlocCompositionFoyer from "../../../features/familles/components/details/BlocCompositionFoyer";
import BlocPersonnesMorales from "../../../features/familles/components/details/BlocPersonnesMorales";
import BlocOrganigramme from "../../../features/familles/components/details/BlocOrganigramme";
import BlocIdentite from "../../../features/familles/components/details/BlocIdentite";
import BlocLogement from "../../../features/familles/components/details/BlocLogement";
import BlocObjectifs from "../../../features/familles/components/details/BlocObjectifs";
import BlocPatrimoine from "../../../features/familles/components/details/BlocPatrimoine";
import BlocRevenus from "../../../features/familles/components/details/BlocRevenus";
import BlocDirigeants from "../../../features/familles/components/details/BlocDirigeants";
import BlocCoordonnees from "../../../features/familles/components/details/BlocCoordonnees";
import BlocFinancier from "../../../features/familles/components/details/BlocFinancier";
import BlocDocuments from "../../../features/familles/components/details/BlocDocuments";
import BlocEngagements from "../../../features/familles/components/details/BlocEngagements";
import FamilleStatus from "../../../features/familles/components/details/FamilleStatus";
import FamilleInteractions from "../../../features/familles/components/details/FamilleInteractions";
import FamilleAudit from "../../../features/familles/components/FamilleAudit";
import SocieteAudit from "../../../features/familles/components/SocieteAudit";
import ArchiveDossierModal from "../../../features/familles/components/details/ArchiveDossierModal";

// Composants UI Design System
import Loader from "../../../components/Loader";
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/Badge";
import { useNotification } from "../../../context/NotificationContext";

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useNotification();
  const [entity, setEntity] = useState(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Composition");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Tentative de récupération directe avec enrichissement backend
        const resFamille = await api.get(`/familles/${id}`).catch(() => null);
        if (resFamille?.data) {
          setEntity(resFamille.data);
          return;
        }

        // Sinon recherche globale
        const familles = await getFamilles();
        const societes = await getSocietes();
        const found = [...familles, ...societes].find((e) => e._id === id || e.id === id);
        setEntity(found);
      } catch (err) {
        console.error("Erreur chargement dossier :", err);
      }
    };
    fetchData();
  }, [id]);

  const handleArchiveConfirm = async (archiveData) => {
    try {
      await archiveFamille(id, archiveData);
      showToast("Dossier archivé conformément aux obligations réglementaires.", "success");
      navigate(isSociete ? "/prospects/societes" : "/prospects/familles");
    } catch (e) {
      console.error("Erreur lors de l'archivage :", e);
      showToast(e?.response?.data?.message || "Erreur lors de l'archivage du dossier.", "error");
      throw e;
    }
  };

  if (!entity) return <Loader />;

  const isSociete = entity.type === "Personne Morale";

  // Configuration des onglets avec icônes
  const tabsFamille = [
    { id: "Composition", label: "Membres", icon: Users },
    { id: "Organigramme", label: "Arbre & Liens", icon: Network },
    { id: "PersonnesMorales", label: "Sociétés (IS)", icon: Building2 },
    { id: "Patrimoine", label: "Patrimoine", icon: Landmark },
    { id: "Revenus", label: "Revenus & Fiscalité", icon: TrendingUp },
    { id: "Logement", label: "Immobilier", icon: Home },
    { id: "Objectifs", label: "Objectifs", icon: Target },
    { id: "Engagements", label: "Engagements", icon: Briefcase },
  ];

  const tabsMorale = [
    { id: "Identité", label: "Identité", icon: Building2 },
    { id: "Organigramme", label: "Arbre de Détention", icon: Network },
    { id: "Dirigeants", label: "Dirigeants", icon: Users },
    { id: "Coordonnées", label: "Coordonnées", icon: Home },
    { id: "Financier", label: "Financier", icon: TrendingUp },
    { id: "Documents", label: "Documents", icon: FileText },
    { id: "Engagements", label: "Engagements", icon: Briefcase },
  ];

  const tabs = isSociete ? tabsMorale : tabsFamille;

  // Ajustement de l'onglet actif par défaut si bascule
  const currentTabExists = tabs.some(t => t.id === activeTab);
  const effectiveActiveTab = currentTabExists ? activeTab : tabs[0].id;

  const renderTabContent = () => {
    if (!isSociete) {
      switch (effectiveActiveTab) {
        case "Organigramme": return <BlocOrganigramme type="Famille" rootId={entity.id || id} />;
        case "PersonnesMorales": return <BlocPersonnesMorales data={entity} />;
        case "Patrimoine": return <BlocPatrimoine data={entity} />;
        case "Revenus": return <BlocRevenus data={entity} />;
        case "Logement": return <BlocLogement data={entity} />;
        case "Objectifs": return <BlocObjectifs data={entity} />;
        case "Engagements": return <BlocEngagements data={entity} />;
        case "Composition":
        default: return <BlocCompositionFoyer data={entity} />;
      }
    } else {
      switch (effectiveActiveTab) {
        case "Organigramme": return <BlocOrganigramme type="Societe" rootId={entity.id || id} />;
        case "Dirigeants": return <BlocDirigeants data={entity} />;
        case "Coordonnées": return <BlocCoordonnees data={entity} />;
        case "Financier": return <BlocFinancier data={entity} />;
        case "Documents": return <BlocDocuments data={entity} />;
        case "Engagements": return <BlocEngagements data={entity} />;
        default: return <BlocIdentite data={entity} />;
      }
    }
  };

  const initiales = isSociete
    ? entity.denomination?.slice(0, 2).toUpperCase()
    : `${entity.prenom?.charAt(0) ?? ""}${entity.nomFamille?.charAt(0) ?? entity.nom?.charAt(0) ?? ""}`.trim().toUpperCase() || "F";

  const nomAffiche = isSociete 
    ? entity.denomination 
    : `${entity.prenom || ''} ${entity.nomFamille || entity.nom || ''}`.trim() || "Famille";

  const primaryContact = entity.membres?.find(m => m.isPrimaryContact);

  const handleNavigateAllocation = () => {
    navigate("/outils/allocation", {
      state: {
        clientId: entity.id || entity._id,
        clientName: nomAffiche,
        clientType: isSociete ? "societe" : "famille"
      }
    });
  };

  const handleNavigateImpots = () => {
    // 1. Déduction du statut fiscal
    let statut = 'celibataire';
    const regime = (entity.regimeMatrimonial || entity.situationFamiliale || '').toLowerCase();
    if (regime.includes('mari')) {
      statut = 'marie';
    } else if (regime.includes('pacs')) {
      statut = 'pacse';
    } else if (regime.includes('divorc')) {
      statut = 'divorce';
    } else if (Array.isArray(entity.membres) && entity.membres.some(m => ['CONJOINT', 'EPOUX', 'EPOUSE', 'PARTENAIRE'].includes(m.role?.toUpperCase()))) {
      statut = 'marie';
    }

    // 2. Nombre d'enfants à charge
    let enfants = 0;
    if (Array.isArray(entity.membres)) {
      const enfantsMembres = entity.membres.filter(m => ['ENFANT', 'ENFANT_CHARGE'].includes(m.role?.toUpperCase()));
      enfants = enfantsMembres.length > 0 ? enfantsMembres.length : (Number(entity.nombreEnfants) || 0);
    } else {
      enfants = Number(entity.nombreEnfants) || 0;
    }

    // 3. Revenus nets imposables
    let revenus = Number(entity.revenusAnnuels) || 0;
    if (!revenus && Array.isArray(entity.membres)) {
      revenus = entity.membres.reduce((acc, m) => acc + (Number(m.revenus) || Number(m.salaire) || 0), 0);
    }

    // 4. Déductions / charges annuelles
    const deductions = Number(entity.chargesAnnuelles) || Number(entity.deductions) || 0;

    navigate("/outils/impots", {
      state: {
        clientId: entity.id || entity._id,
        clientName: nomAffiche,
        fiscalData: {
          statut,
          enfants,
          revenus,
          deductions
        }
      }
    });
  };

  return (
    <PageAnimator>
      <div className="flex flex-col min-h-[92vh] bg-workspace px-4 sm:px-6 py-6 overflow-y-auto">
        {/* Fil d'Ariane & Actions d'en-tête */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <Breadcrumbs 
            items={[
              { label: isSociete ? 'Sociétés' : 'Familles', path: isSociete ? '/prospects/societes' : '/prospects/familles' },
              { label: nomAffiche }
            ]} 
          />

          <div className="flex flex-wrap items-center gap-2">
            {/* Audit patrimonial conditionné : affiché uniquement si non complété */}
            {!entity.dateAudit && (
              <Button
                variant="accent"
                size="sm"
                onClick={() => setShowAuditModal(true)}
                className="flex items-center gap-1.5 shadow-hp-sm text-xs sm:text-sm"
              >
                <ClipboardList className="w-4 h-4" />
                Audit Patrimonial
              </Button>
            )}

            <Button
              variant="secondary"
              size="sm"
              onClick={handleNavigateAllocation}
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <Sliders className="w-4 h-4" />
              Allocation
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={handleNavigateImpots}
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <Banknote className="w-4 h-4" />
              Imposition
            </Button>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
          {/* Colonne Principale (Onglets et Contenu) */}
          <div className="lg:col-span-2 space-y-5">
            {/* Header du Dossier */}
            <div className="workspace-card p-5 sm:p-6 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
                <div className="flex items-center gap-4">
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="w-13 h-13 rounded-2xl bg-gradient-to-br from-[var(--hp-ink-800)] to-[var(--hp-ink-950)] text-white font-extrabold text-xl flex items-center justify-center shadow-hp-md shrink-0 border border-white/10 ring-2 ring-workspace"
                  >
                    {initiales}
                  </motion.div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-primary">{nomAffiche}</h1>
                      <Badge variant="info">{entity.statut || "Client Actif"}</Badge>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5 mt-1 text-xs text-subtle">
                      <span className="font-mono font-medium text-muted">
                        Réf. {entity.reference || (entity.id ? `DOS-${String(entity.id).slice(0, 8).toUpperCase()}` : "DOS-NC")}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1 font-semibold text-secondary">
                        <UserCheck className="w-3.5 h-3.5" />
                        Point d'entrée : {primaryContact ? `${primaryContact.prenom} ${primaryContact.nom}` : (entity.prenom ? `${entity.prenom} ${entity.nom}` : "Déclarant 1")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsArchiveModalOpen(true)}
                    className="flex items-center gap-1.5 text-xs text-subtle hover:text-amber-700 hover:border-amber-300 hover:bg-amber-50 rounded-[var(--hp-radius-md)] transition-colors"
                    title="Archiver le dossier (Fin de mission réglementaire)"
                  >
                    <Archive className="w-4 h-4 text-amber-600" />
                    <span>Archiver</span>
                  </Button>
                </div>
              </div>
              
              {/* Barre de Navigation par Onglets */}
              <nav className="border-b border-workspace w-full">
                <div className="-mb-px flex space-x-1 sm:space-x-2 overflow-x-auto pb-1 scrollbar-none">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = effectiveActiveTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`relative flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold rounded-t-lg transition-colors whitespace-nowrap ${
                          isActive
                            ? "text-[var(--hp-color-accent-text)] bg-surface-muted/50"
                            : "text-subtle hover:text-primary hover:bg-surface-muted/30"
                        }`}
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span>{tab.label}</span>
                        {isActive && (
                          <motion.div
                            layoutId="activeTabIndicator"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--hp-color-accent)]"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </nav>
            </div>
            
            {/* Contenu de l'onglet actif avec animation */}
            <AnimatePresence mode="wait">
              <motion.div
                key={effectiveActiveTab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                {renderTabContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Colonne Latérale Droite (Tâches & Interactions) */}
          <div className="space-y-5">
            <FamilleStatus famille={entity} />
            <FamilleInteractions famille={entity} />
          </div>
        </div>

        {/* Modales d'audit */}
        {isSociete ? (
          <SocieteAudit open={showAuditModal} societe={entity} onClose={() => setShowAuditModal(false)} />
        ) : (
          <FamilleAudit open={showAuditModal} famille={entity} onClose={() => setShowAuditModal(false)} />
        )}

        {/* Modale d'Archivage Réglementaire (CIF/AMF) */}
        <ArchiveDossierModal
          isOpen={isArchiveModalOpen}
          onClose={() => setIsArchiveModalOpen(false)}
          onConfirm={handleArchiveConfirm}
          nomDossier={nomAffiche}
          reference={entity.reference || (entity.id ? `DOS-${String(entity.id).slice(0, 8).toUpperCase()}` : "DOS-NC")}
        />
      </div>
    </PageAnimator>
  );
}
